import { RSAA } from 'redux-api-middleware'
import { getApiPath } from 'sidecar-utility-library'

import { types, initialState } from './types'

/**
 * Fetch NDC suggestions from the care API
 *
 * @param {string} query - The NDC code to search for
 * @returns RSAA action for fetching NDC suggestions
 */
export const fetchNdcSuggestions = (query = '') => ({
  [RSAA]: {
    endpoint: getApiPath(
      'care',
      `suggest?code=${query.toUpperCase()}&codeTypes=ndc`,
    ),
    method: 'GET',
    types: [
      types.FETCH_NDC_SUGGESTIONS_REQUEST,
      types.FETCH_NDC_SUGGESTIONS_SUCCESS,
      types.FETCH_NDC_SUGGESTIONS_ERROR,
    ],
  },
})

/**
 * Fetch pharmacy prescription form data for editing (Fix-a-Bill feature) Gets
 * the original/current pharmacy prescription data from the expense
 *
 * @param {string} expenseUuid - The expense UUID
 * @returns RSAA action for fetching pharmacy prescription form
 */
export const fetchPharmacyPrescriptionForm = expenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `expenses/${expenseUuid}/pharmacy-prescription-form`,
    ),
    method: 'GET',
    types: [
      types.FETCH_PHARMACY_PRESCRIPTION_FORM_REQUEST,
      types.FETCH_PHARMACY_PRESCRIPTION_FORM_SUCCESS,
      types.FETCH_PHARMACY_PRESCRIPTION_FORM_ERROR,
    ],
  },
})

/**
 * Submit pharmacy prescription form edits for fix-a-bill feature
 *
 * @param {string} expenseUuid - The expense UUID
 * @param {object} payload - The form data with edits
 * @returns RSAA action for submitting pharmacy prescription form
 */
export const submitPharmacyPrescriptionForm = (expenseUuid, payload) => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `expenses/${expenseUuid}/pharmacy-prescription-form`,
    ),
    method: 'PUT',
    body: JSON.stringify(payload),
    types: [
      types.SUBMIT_PHARMACY_PRESCRIPTION_FORM_REQUEST,
      {
        type: types.SUBMIT_PHARMACY_PRESCRIPTION_FORM_SUCCESS,
        meta: { expenseUuid },
      },
      types.SUBMIT_PHARMACY_PRESCRIPTION_FORM_ERROR,
    ],
  },
})

export const clearPharmacyPrescriptionFormError = () => ({
  type: types.CLEAR_PHARMACY_PRESCRIPTION_FORM_ERROR,
})

export const clearPharmacyPrescriptionFormData = () => ({
  type: types.CLEAR_PHARMACY_PRESCRIPTION_FORM_DATA,
})

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.FETCH_PHARMACY_PRESCRIPTION_FORM_REQUEST: {
      return {
        ...state,
        pharmacyPrescriptionForm: {
          ...state.pharmacyPrescriptionForm,
          busy: true,
          error: null,
        },
      }
    }

    case types.FETCH_PHARMACY_PRESCRIPTION_FORM_SUCCESS: {
      return {
        ...state,
        pharmacyPrescriptionForm: {
          ...state.pharmacyPrescriptionForm,
          entity: payload,
          busy: false,
          error: null,
        },
      }
    }

    case types.FETCH_PHARMACY_PRESCRIPTION_FORM_ERROR: {
      return {
        ...state,
        pharmacyPrescriptionForm: {
          ...state.pharmacyPrescriptionForm,
          busy: false,
          error: payload?.response?.message || 'Failed to load form data',
        },
      }
    }

    case types.SUBMIT_PHARMACY_PRESCRIPTION_FORM_REQUEST: {
      return {
        ...state,
        pharmacyPrescriptionForm: {
          ...state.pharmacyPrescriptionForm,
          submitting: true,
          submitError: null,
        },
      }
    }

    case types.SUBMIT_PHARMACY_PRESCRIPTION_FORM_SUCCESS: {
      return {
        ...state,
        pharmacyPrescriptionForm: {
          ...state.pharmacyPrescriptionForm,
          submitting: false,
          submitError: null,
        },
      }
    }

    case types.SUBMIT_PHARMACY_PRESCRIPTION_FORM_ERROR: {
      return {
        ...state,
        pharmacyPrescriptionForm: {
          ...state.pharmacyPrescriptionForm,
          submitting: false,
          submitError: payload?.response?.message || 'Failed to submit form',
        },
      }
    }

    case types.CLEAR_PHARMACY_PRESCRIPTION_FORM_ERROR: {
      return {
        ...state,
        pharmacyPrescriptionForm: {
          ...state.pharmacyPrescriptionForm,
          error: null,
        },
      }
    }

    case types.CLEAR_PHARMACY_PRESCRIPTION_FORM_DATA: {
      return {
        ...state,
        pharmacyPrescriptionForm: initialState.pharmacyPrescriptionForm,
      }
    }

    // NDC Suggestions
    case types.FETCH_NDC_SUGGESTIONS_REQUEST: {
      return {
        ...state,
        pharmacyPrescriptionForm: {
          ...state.pharmacyPrescriptionForm,
          ndcSuggestions: {
            ...state.pharmacyPrescriptionForm.ndcSuggestions,
            busy: true,
            error: null,
          },
        },
      }
    }

    case types.FETCH_NDC_SUGGESTIONS_SUCCESS: {
      return {
        ...state,
        pharmacyPrescriptionForm: {
          ...state.pharmacyPrescriptionForm,
          ndcSuggestions: {
            busy: false,
            entities: payload || [],
            error: null,
          },
        },
      }
    }

    case types.FETCH_NDC_SUGGESTIONS_ERROR: {
      return {
        ...state,
        pharmacyPrescriptionForm: {
          ...state.pharmacyPrescriptionForm,
          ndcSuggestions: {
            ...state.pharmacyPrescriptionForm.ndcSuggestions,
            busy: false,
            error:
              payload?.response?.message || 'Failed to load NDC suggestions',
          },
        },
      }
    }

    default:
      return state
  }
}
